export default {
  copyEvaluations: `
    <h2>1. General Description</h2>
    <p>
      A feature is introduced that allows copying {evaluations} between different subjects in a
      bidirectional manner. This facilitates the creation of evaluation structures by reusing those already
      existing in other subjects linked to the same graduation profile or {baseMatter}, avoiding repetitive
      manual creation.
    </p>
    <p>
      The feature is presented through a three-column modal (Source, Destination and Preview) that
      allows the user to select which {evaluations} to copy, which to delete or keep from the destination, and
      adjust the weightings before confirming the operation.
    </p>

    <h2>2. Available Copy Operations</h2>
    <p>
      There are three copy operations, each accessible from a different context. All share the
      same reusable modal with bidirectional capability.
    </p>

    <h3>2.1 Copy between {egressProfileMatters} ({egressProfileMatters} to {egressProfileMatters})</h3>
    <p>
      Allows copying {evaluations} from the current subject (within a graduation profile) to another
      subject linked to a different graduation profile, as long as both share the same {baseMatter}.
    </p>
    <p><strong>Visibility conditions:</strong></p>
    <ul>
      <li>There must be at least one other subject of the same type not linked in another graduation profile.</li>
      <li>The user must have editing permissions and be a superuser.</li>
    </ul>
    <p>
      <strong>Where to find it:</strong> In the {matterProgram} view, {evaluations} section,
      "Copy {evaluations}" button and then select "Import/Export to another Graduation Profile".
    </p>

    <h3>2.2 Copy between {egressProfileMatters} and {baseMatter} ({egressProfileMatters} to {baseMatter})</h3>
    <p>
      Allows copying {evaluations} between the current subject (within a graduation profile) and its
      {baseMatter}. This is useful for synchronizing {evaluations} between the base template and derived subjects.
    </p>
    <p>
      <strong>Where to find it:</strong> In the {matterProgram} view, {evaluations} section,
      "Copy {evaluations}" button and then select "Import/Export to {baseMatter}".
    </p>
    <p><strong>Visibility conditions:</strong></p>
    <ul>
      <li>The user must have editing permissions and be a superuser.</li>
    </ul>

    <h3>2.3 Copy from {baseMatter} to {egressProfileMatters} ({baseMatter} to {egressProfileMatters})</h3>
    <p>
      Allows copying {evaluations} from the {baseMatter} to a {egressProfileMatters}. This
      operation is accessed from the {baseMatter} view when a graduation profile context is associated.
    </p>
    <p><strong>Visibility conditions:</strong></p>
    <ul>
      <li>The user must have editing permissions and be a superuser.</li>
      <li>There must be at least one other subject of the same type not linked in another graduation profile.</li>
    </ul>

    <h3>Operations Summary</h3>
    <table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%">
      <thead>
        <tr>
          <th>Operation</th>
          <th>Default Source</th>
          <th>Default Destination</th>
          <th>Requires Superuser</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{egressProfileMatters} to {egressProfileMatters}</td>
          <td>Current subject ({egressProfileMatters})</td>
          <td>Another subject from another Graduation Profile</td>
          <td>Yes</td>
        </tr>
        <tr>
          <td>{egressProfileMatters} to {baseMatter}</td>
          <td>Current subject ({egressProfileMatters})</td>
          <td>{baseMatter}</td>
          <td>Yes</td>
        </tr>
        <tr>
          <td>{baseMatter} to {egressProfileMatters}</td>
          <td>{baseMatter}</td>
          <td>{egressProfileMatters}</td>
          <td>Yes</td>
        </tr>
      </tbody>
    </table>

    <h2>3. Copy Modal Interface</h2>
    <p>
      The copy modal presents a three-column interface that allows you to visualize and control the copy
      process in real time.
    </p>

    <h3>3.1 Source Column ({evaluations} to Import/Export)</h3>
    <p>Shows the available {evaluations} in the source subject, sorted alphabetically.</p>
    <ul>
      <li>Each evaluation shows its name, weighting (%) and associated instruments.</li>
      <li>The user selects with checkboxes the {evaluations} they want to copy to the destination.</li>
      <li>"Select all" and "Deselect all" buttons to speed up selection.</li>
      <li>If an evaluation has linked instruments, these are shown as a list below the name.</li>
    </ul>

    <h3>3.2 Destination Column ({evaluations} to Update)</h3>
    <p>Shows the {evaluations} currently existing in the destination subject.</p>
    <ul>
      <li>The user can select destination {evaluations} to delete them as part of the operation.</li>
      <li>Unselected {evaluations} are kept and appear in the preview.</li>
      <li>If no destination evaluation is selected, the copy only adds new {evaluations} without deleting anything.</li>
    </ul>

    <h3>3.3 Result Preview Column</h3>
    <p>
      Shows in real time how the destination subject will look after applying the copy. Combines the
      {evaluations} kept from the destination with the new ones added from the source.
    </p>
    <ul>
      <li>New {evaluations} (from the source) are shown in green and bold text.</li>
      <li>Existing {evaluations} that are kept are shown in normal text.</li>
      <li>Each evaluation has an editable weighting field to adjust the percentage before confirming.</li>
      <li>At the bottom of the column the total weightings are shown and validated to not exceed 100%.</li>
    </ul>

    <h2>4. Bidirectional Functionality (Swap Direction)</h2>
    <p>
      All copy operations are bidirectional. At the top of the modal the current copy direction is shown
      (e.g. "Subject A -> Subject B") along with a button to swap the direction.
    </p>
    <p>
      When pressing "Swap Direction", the subject that was the source becomes the destination and vice versa.
    </p>
    <ul>
      <li>When swapping direction, selections and adjusted weightings are reset.</li>
      <li>The modal description updates automatically to reflect the new direction.</li>
      <li>For {egressProfileMatters} to {baseMatter} copy, the modal shows a yellow alert (warning) since modifying the {baseMatter} can impact multiple derived subjects.</li>
    </ul>

    <h2>5. Destination Subject Selector</h2>
    <p>
      In {egressProfileMatters} to {egressProfileMatters} and {baseMatter} to {egressProfileMatters} operations,
      the modal includes a dropdown selector at the top to choose the destination subject.
    </p>
    <ul>
      <li>The selector shows all subjects of the same type in other graduation profiles.</li>
      <li>The current subject and those with the "Link Subject to Itself" option enabled are excluded.</li>
      <li>While no subject is selected, the "Copy {evaluations}" button remains disabled.</li>
      <li>When selecting a subject, its {evaluations} are automatically loaded in the destination column.</li>
    </ul>
    <p>
      In the {egressProfileMatters} to {baseMatter} operation, no selector is needed since the {baseMatter}
      is determined automatically by context.
    </p>

    <h2>6. Weighting Validation</h2>
    <p>
      The system validates that the total sum of weightings in the preview does not exceed 100%. If it
      does, an error message is shown in red and the confirmation button is disabled.
    </p>
  `,
};
